import _ from 'lodash';

const TEXTS = {
  en: {
    sample: {
      title: 'Hello {{name}}',
    },
    menu: {
      solutions: 'Solutions',
      startups: { title: 'Startups and brands', subtitle: 'Simplify your innovation journey' },
      agencies: { title: 'Digital agencies', subtitle: 'Empower your agency' },
      publishers: { title: 'Publishers', subtitle: 'Engage readers, increase revenue' },
      product: 'Product',
      editor: { title: 'Designer', subtitle: 'Design without limits' },
      flow: { title: 'Flow', subtitle: 'Personalize user journeys' },
      cardsFormat: { title: 'Cards Format', subtitle: 'Build experiences users love' },
      collaboration: { title: 'Collaboration', subtitle: 'Work together seamlessly' },
      dataAndAnalytics: 'Data & Analytics',
      pricing: 'Pricing',
      about: 'Our story',
      analytics: { title: 'Analytics', subtitle: 'Insights that drive results' },
      dpa: 'Data Privacy',
      tos: 'Terms of Service',
      dataCollection: { title: 'Data Collection', subtitle: 'Your data, your control' },
      segments: { title: 'Create Segments', subtitle: 'Personalize advertising efforts' },
      careers: 'Careers',
      pressCenter: 'Press Center',
      privacy: 'Privacy Policy',
      inspiration: 'Get Inspired',
      ai: 'AI engine',
      hosting: { title: 'Hosting' },
      security: { title: 'Security' },
      api: { title: 'API', subtitle: 'Secure API for enterprises' },
    },
    mainPage: {
      title: 'The no-code platform for engaging data collection enhanced by AI',
      titleMobile: 'The no-code platform for engaging data collection enhanced by AI',
    },
    header: {
      startBtnLabel: 'Start Creating',
      loginBtnLabel: 'Log In',
      startBtnLabelMobile: 'Start',
    },
    SCEditor: {
      title: 'Let’s meet Storycards Editor',
    },
    analytics: {
      topSection: {
        title: 'Real-time insights for better engagement',
        subTitle:
          'The Storycards analytics dashboard helps you track and understand your audience’s engagement levels.',
      },
      subTitle: 'The Storycards analytics dashboard helps you track and understand your audience’s engagement levels.',
      subTitleDescription: `Storycards analytics provides a complete breakdown of your data, tracking performance by stories and cards. Analyze user behavior, their choices, completion rates, and lead submissions to uncover what resonates with your audience.`,
      infoSection: [
        {
          title: 'Real time insights',
          text: `Storycards analytics updates in real time, giving you instant feedback on how your story performs. Use these insights to make data-driven changes and boost engagement effectively.`,
        },
        {
          title: 'Track drop-offs and optimize your flow',
          text: 'Identify drop-off points in your story, refine the user flow, and keep your audience engaged. Analyze performance data to make impactful adjustments and improve results.',
        },
        {
          title: 'Custom trackers',
          text: 'Integrate any analytics tool of your choice directly into your Storycards. Track the metrics that matter most to your business and gain complete control over your data.',
        },
        {
          title: 'Event tracking',
          text: `Track custom user interactions, from clicks to form submissions, with tailored event tracking. Gain deeper insights into what drives engagement.`,
        },
        {
          title: 'A/B testing insights',
          text: 'Compare variations of your stories to discover what works best. Use the data to refine your content and maximize engagement.',
        },
        {
          title: 'Device and platform analysis',
          text: `Understand how users interact with your stories across devices and platforms. Optimize your design for mobile, desktop, or tablet to ensure seamless engagement everywhere.`,
        },
        {
          title: 'Geographic performance',
          text: `Track where your audience is located and understand engagement levels by region. Use these insights to tailor your content to specific locations.`,
        },
        {
          title: 'Completion rate analytics',
          text: `See how many users finish your stories and what might be causing drop-offs. Use this data to refine your storytelling for better engagement.`,
        },
        {
          title: 'User journey mapping',
          text: `Visualize how users navigate through your stories. Identify common paths, bottlenecks, and opportunities for improvement.`,
        },
        {
          title: 'Exportable reports',
          text: `Generate and download detailed analytics reports to share with your team or stakeholders. Get insights in a clear, actionable format.`,
        },
        {
          title: 'Seamless Integration with Google Analytics',
          text: 'Integrate your Google Analytics account to track Storycards data and report custom events tailored to your needs.',
        },
        {
          title: 'Traffic source analysis',
          text: `Identify where your users are coming from, whether it's organic search, paid ads, social media, or direct traffic. Understand which channels drive the best results.`,
        },
        {
          title: 'Video engagement tracking',
          text: 'Monitor video performance with metrics such as average watch time, total views, and completion rates.',
        },
        {
          title: 'Social media engagement tracking',
          text: 'Track shares from your audience on social media to measure the social impact of your content.',
        },
      ],
    },
    segments: {
      topSection: {
        title: 'Know your users, build better relationships!',
        subTitle: 'The ideal way to know more about your users and create a better relationship with them.',
      },
      subTitle:
        'Create highly targeted audience segments based on user choices and launch advertising campaigns that truly resonate.',
      subTitleDescription: `Storycards allows you to embed marketing pixel code on any element, answer, or card. This pixel will activate only when a user selects a specific answer or reaches a particular card based on their choices.\n\nWhile there are many marketing tools available, the ability to create precise and focused audience segments makes Storycards the leading system for generating targeted audiences.\n\nYou can create a poll, trivia game, or simply ask your users a question, then segment them as an audience based on their choices. Use these segments to target ads in your app or website, tailored to the decisions users make within the story.`,
      infoSection: [
        {
          title: 'Smart audience segmentation',
          text: 'Effortlessly create detailed audience segments based on user choices, actions, and preferences within your stories.',
        },
        {
          title: 'Dynamic pixel activation',
          text: 'Embed marketing pixels on specific elements and trigger them based on user interactions for precise tracking.',
        },
        {
          title: 'Behavior-based targeting',
          text: 'Segment your audience by analyzing how users interact with polls, trivia, and questions in real-time.',
        },
        {
          title: 'Personalized ad campaigns',
          text: 'Use audience segments to deliver hyper-personalized ad campaigns tailored to user decisions.',
        },
        {
          title: 'Customizable triggers',
          text: 'Set up custom triggers for segments using specific answers, actions, or cards, ensuring highly targeted data collection.',
        },
        {
          title: 'Seamless data integration',
          text: 'Export segmented user data to your preferred CRM or third-party tools for further analysis and use.',
        },
        {
          title: 'In-story decision tracking',
          text: 'Track user decisions at every step and create actionable insights for audience segmentation.',
        },
        {
          title: 'Enhanced engagement analytics',
          text: 'Gain detailed insights into user behavior and engagement, optimizing future segmentation strategies.',
        },
        {
          title: 'Multi-platform audience sync',
          text: 'Synchronize your audience segments across platforms, ensuring consistent targeting and messaging.',
        },
        {
          title: 'Real-time segment updates',
          text: 'Automatically update audience segments as users interact with your stories for instant campaign optimization.',
        },
      ],
    },
    cardsFormat: {
      title: 'Engage your audience with a variety of content experiences that people love',
      heroInfo: {
        title:
          'Mix and match from a variety of our products such as quiz, poll, yes/no, Leads Form, and more to create a unique engagement experience.',
        text: 'Start with an interesting opening screen, then create engagement with your audience using different engagement cards and even collect information about your users with a lead form. ',
      },
      heroInfo2: {
        title: 'Engagement Logic',
        items: [
          {
            title: 'Smart scoring system',
            text: 'You can define multiple correct answers or allocate different amounts of points for correct answers by cards, and then show your users different content and cards according to the points they gained.',
          },
          {
            title: 'Navigate by answers',
            text: 'Give a different score to each answer and lead users to different results. Then, show your users different content according to their answers\\results.',
          },
          {
            title: 'Define repetition',
            text: 'Each product can be defined as a one-time per user activity, or as an unlimited participation activity.',
          },
          {
            title: 'Multiple flows',
            text: "Create Multiple flows according to the users' choices on each card. Mix Quiz with different types of cards.",
          },
          {
            title: 'Ask what you want',
            text: 'Add any fields to your form, and name it as you choose. Then define which fields are mandatory and add Form validations.',
          },
          {
            title: 'Share user result',
            text: 'Show the user his results at any stage you want and encourage him to share it.',
          },
        ],
      },
      cardTypes: {
        title: 'Mix and match from a variety of our products',
        register: {
          title: 'Registration',
          text: 'Start with an interesting opening screen, then create engagement with your audience using different engagement cards and even collect information about your users with a lead form.',
        },
        info: {
          title: 'Info',
          text: "Since you still want to tell a story amongst all the fun activities.\n\nYou can open your story with a motivational call for action and a CTA button, and also finish it by showing a specific text depending on the user's result. The information card is your way to show any content you want during the engagement.",
        },
        form: {
          title: 'Leads Form',
          text: 'Dramatically Increase the number of leads and get better quality information.\n\nA great way to increase your leads is to mix leads from inside the engaging products, the users feel comfortable sharing information while they play and are involved in your content. ',
        },
        personality: {
          title: 'Personality Quiz',
          text: "Go viral with a fun, engaging test that reveals something new about your audience.\n\nPeople always want to know more about themselves and how they are doing compared to others. Users love it. It's fun and a highly shareable format.",
        },
        trivia: {
          title: 'Trivia',
          text: "Check your users' knowledge and recommend further action based on how much they know.\n\nUsers love it. It's fun and highly shareable. Trivia improves your engagement and increases the time users spend on your site by more than 80%.",
        },
        poll: {
          title: 'Poll',
          text: 'People always have an opinion. Ask the right questions, know more about your users and show the results.\n\nThe best way to involve your users with your content increases the time users spend on your site and know more about your users.',
        },
        thisOrThat: {
          title: 'This or That',
          text: 'People always have an opinion. Ask the right questions, know more about your users and show the results.\n\nThe best way to involve your users with your content increases the time users spend on your site and know more about your users.',
        },
        trueOrFalse: {
          title: 'True or False',
          text: 'Check the knowledge of your users with a great engaging swipe card.\n\nA Great engaging card type with drag and drop way.',
        },
      },
    },
    editorPage: {
      topSection: {
        title: `Your Playground for Creative Freedom`,
        subtitle:
          'Empower your creative vision with a platform that makes designers the true rulers of their organizations—no code required.',
      },
      videoSectionUnderText:
        '<p>Create premium digital products to capture data and engage users using our flexible drag-and-drop visual canvas. No code needed.</p>',
      infoItems: {
        designItTitle: 'Design as you want ',
        designItText: `Absolute control and freedom to design just the way you want it. Start with a template or from scratch and build your custom design. Smooth drag and drop, no coding required.`,
        notMoreTitle: 'Not more of the same',
        notMoreText:
          'Storycards gives you the ability to create any concept and the freedom to design as you want, with no limits. Cutting-edge responsive design with smooth drag and drop, no code needed.',
        designResponsiveTitle: 'Responsive design',
        designResponsiveText: `Design any element differently for Mobile, Tablet or Desktop and adjust layouts by platform and device with an easy preview feature.`,
        fontsTitle: 'Use your own fonts',
        fontsText: `You can choose from our fonts gallery or upload your own fonts to match your brand. Adjust everything from line height to letter spacing. `,
        contentTitle: 'Add your content',
        contentText: `Work directly with our editor to add your content, texts and images into your card’s design, so you can see how your content looks as you design it in real time.`,
        platformTitle: 'Tailor your content by platform',
        platformText: `Adjust or change parts of your text and content between the mobile, tablet and desktop platforms, so a long title in the desktop version can become an alternate shorter text in the mobile version.`,
        createReusableTitle: 'Create reusable components',
        createReusableText: `Every element in your story can become a reusable component that will be seamlessly updated across your entire story, so you can change something on one card and it will be updated on all other cards, with a single edit.`,
        gridTitle: 'Grid layouts and boxes',
        gridText: `Build grid layouts and boxes to your design and align them as desired with the editor menu. You also always have the option to write your own CSS code directly into the editor and style every element to perfection.`,
        globalStickyTitle: 'Create global and sticky elements',
        globalStickyText: `Define your headers, footers or any other element as global elements that will automatically display on all your Storycards. Apply element as “pin to card”, so it stays floating during scroll.`,
        animationTitle: 'Animation',
        animationText: `Choose any element of your card that you want to animate, and customize it's duration and delay.`,
        shareTitle: 'Add share buttons',
        shareText: `Add, design and place share buttons on your story, through which your users will be able to share the product with their friends.`,
        seoTitle: 'SEO tools',
        seoText: `Add your meta data with SEO tools, enter your story title and description.`,
        userTitle: 'Define repetition',
        userText: `Each product can be defined as a one time per user activity, or as an activity with unlimited participation.`,
        ownCodeTitle: 'Add your own code',
        ownCodeText: `And if all that's not enough for you, add your custom code on every card.`,
        marketingTitle: 'Marketing pixels',
        marketingText:
          "Adding your marketing pixels gives you the ability to create a custom audience by users' choices. You can add pixel code on any element, answer or card.",
        revisionTitle: 'Revision history',
        revisionText:
          'Everything you do inside the editor and any changes by other users on your team are saved automatically, you can go back at any time and use revision history to publish old versions.',
      },
      scrollSection: [
        {
          title: 'Responsive design',
          desc: 'Tailor the experience for various platforms with our auto-layouts and customizing capabilities. Change design settings for components or switch their content per platform.',
          videoSrc: {
            desktop: 'https://assts.stories.sc/website/desktop/get_personal.mp4',
            mobile: 'https://assts.stories.sc/website/mobile/get_personal.mp4',
          },
          poster: { desktop: 'https://assts.stories.sc/website/poster/get_personal.jpg' },
        },
        {
          title: 'Design, Save, Reuse',
          desc: 'Create reusable components to accelerate the design. Every element in your story can become a reusable component with global editing capabilities.',
          videoSrc: {
            desktop: 'https://assts.stories.sc/website/desktop/scoring.mp4',
            mobile: 'https://assts.stories.sc/website/mobile/scoring.mp4',
          },
          poster: { desktop: 'https://assts.stories.sc/website/poster/scoring.jpg' },
        },
        {
          title: 'Auto layouts, grid and boxes',
          desc: 'Simply set parameters, and watch your components adapt across breakpoints., Wrap your elements with boxes that grow and shrink by your content.',
          videoSrc: {
            desktop: 'https://assts.stories.sc/website/desktop/url_parameters.mp4',
            mobile: 'https://assts.stories.sc/website/mobile/url_parameters.mp4',
          },
          poster: { desktop: 'https://assts.stories.sc/website/poster/url_parameters.jpg' },
        },
        {
          title: 'Personalized journeys',
          desc: `Adapt the design and user experience dynamically based on each user's actions, choices, and preferences within the product.`,
          videoSrc: {
            desktop: 'https://assts.stories.sc/website/desktop/flowcondtion.mp4',
            mobile: 'https://assts.stories.sc/website/mobile/flowcondtion.mp4',
          },
          poster: { desktop: 'https://assts.stories.sc/website/poster/flowcondtion.jpg' },
        },
        {
          title: 'Animations',
          desc: `Animate your elements to create stunning experiences and add Lottie animations with advanced play triggers.`,
          videoSrc: {
            desktop: 'https://assts.stories.sc/website/desktop/flowcondtion.mp4',
            mobile: 'https://assts.stories.sc/website/mobile/flowcondtion.mp4',
          },
          poster: { desktop: 'https://assts.stories.sc/website/poster/flowcondtion.jpg' },
        },
        {
          title: 'Design Smarter, Backed by Data',
          desc: `Storycards empowers designers to craft seamless experiences and make data-driven decisions with actionable insights.`,
          videoSrc: {
            desktop: 'https://assts.stories.sc/website/desktop/flowcondtion.mp4',
            mobile: 'https://assts.stories.sc/website/mobile/flowcondtion.mp4',
          },
          poster: { desktop: 'https://assts.stories.sc/website/poster/flowcondtion.jpg' },
        },
      ],
      infoSection: [
        {
          title: 'Design as You Want',
          text: 'Absolute control and freedom to design just the way you want. Start with a template or from scratch to build your custom design. Smooth drag-and-drop, no coding required.',
        },
        {
          title: 'Responsive Design',
          text: 'Design any element differently for mobile, tablet, or desktop. Adjust layouts for each platform and device with an easy preview feature.',
        },
        {
          title: 'Drag and Drop',
          text: 'Add and position elements freely by dragging and dropping them onto the canvas.',
        },
        {
          title: 'Auto Layouts, Grids, and Boxes',
          text: 'Set parameters and watch your components adapt across breakpoints. Wrap elements in boxes that expand or shrink dynamically based on content.',
        },
        {
          title: 'Use Your Own Fonts',
          text: 'Choose from our font gallery or upload your own fonts to match your brand. Adjust everything from line height to letter spacing.',
        },
        {
          title: 'Personalized Journeys',
          text: `Adapt the design and user experience dynamically based on each user's actions, choices, and preferences within the product.`,
        },
        {
          title: 'Integrated A/B Testing Tools',
          text: 'Easily set up A/B tests directly in the designer to measure and improve product performance.',
        },
        {
          title: 'Overlays',
          text: 'Create pop-ups, tooltips, or menus within cards.',
        },
        {
          title: 'Sliders',
          text: 'Easily create sliders for content in various forms—vertical, horizontal, or as galleries for videos and images. Insert any content into the slider.',
        },
        {
          title: 'AI-Powered Image Creation',
          text: 'Generate custom images using AI prompts directly in the designer. Choose styles, adjust details, and instantly add visuals to your story.',
        },
        {
          title: 'Generative Video Creation',
          text: 'Use AI to create short, engaging videos based on a text prompt or script.',
        },
        {
          title: 'AI-Compatible Templates',
          text: 'Design templates with your unique design system and define parameters for AI. Let content teams quickly create products while maintaining organizational design standards.',
        },
        {
          title: 'Animations',
          text: 'Bring your elements to life with Lottie animations and triggers tied to user actions, such as confetti for correct answers or fade-outs for incorrect ones.',
        },
        {
          title: 'Gradients',
          text: 'Enhance section backgrounds with custom linear, radial, conic, and fluid gradients.',
        },
        {
          title: 'Crop and Resize Images',
          text: 'Easily crop and resize images directly in the designer. Adjust dimensions to fit your layout perfectly, with intuitive editing tools.',
        },
        {
          title: 'Image Auto-Optimize',
          text: 'Automatically optimize images for faster loading times without compromising quality. Ideal for improving performance on all devices.',
        },
        {
          title: 'Image Serve Auto Format',
          text: `Deliver images in the best format (e.g., WebP, PNG, JPEG) automatically based on the user's browser and device capabilities.`,
        },
        {
          title: 'Deliver the Best Image Size by Device',
          text: 'Serve appropriately scaled images tailored for desktop, tablet, or mobile to reduce load times and enhance user experience.',
        },
        {
          title: 'Tailor Your Content by Platform',
          text: 'Adjust or change parts of your text and content for mobile, tablet, and desktop. For example, a long title on desktop can become a shorter alternative for mobile.',
        },
        {
          title: 'Create Reusable Components',
          text: 'Turn every element in your story into reusable components. Update them seamlessly across all cards in one single edit.',
        },
        {
          title: 'Create Global and Sticky Elements',
          text: 'Add headers, footers, CTA buttons, and other sticky elements to enhance navigation.',
        },
        {
          title: 'Text Scaling',
          text: 'Adjust text proportions seamlessly across screen sizes or define custom scaling ranges for precise control.',
        },
        {
          title: 'Custom CSS',
          text: 'Add your own CSS for detailed customization and full design control.',
        },
        {
          title: 'Counters',
          text: 'Create counters that count up or down. Customize their design, animation, and number transitions.',
        },
        {
          title: 'Timer',
          text: 'Add a visual or numerical timer with customizable design. Trigger actions when the timer ends, such as moving to the next step.',
        },
        {
          title: 'Design Forms and Fields',
          text: 'Design fields and buttons for forms freely, down to the level of error messages and placeholders.',
        },
        {
          title: 'Answers Component',
          text: 'Design a versatile answers component that can display text, images, or any other content, with fully customizable styles and states.',
        },
        {
          title: 'State-Based Answers',
          text: 'Define states for answers, including correct, incorrect, pending selection, or disabled, with smooth visual transitions.',
        },
        {
          title: 'Interactive Feedback Messages',
          text: 'Automatically show feedback messages based on user interactions, with support for rich content like icons, images, and text.',
        },
        {
          title: 'Add Share Buttons',
          text: 'Design and place share buttons to enable users to share your product with their friends.',
        },
        {
          title: 'Revision History',
          text: 'Automatically save every change made by you or your team. Revert to previous versions anytime with revision history.',
        },
        {
          title: 'Add Your Own Code',
          text: 'If you need even more flexibility, add custom code to any card.',
        },
        {
          title: 'SEO Tools',
          text: 'Optimize your story for search engines with SEO tools. Enter meta data, titles, and descriptions for improved visibility.',
        },
        {
          title: 'Data Integrations',
          text: 'Integrate external data sources such as APIs or databases to power dynamic and interactive content.',
        },
        {
          title: 'Accessibility Features',
          text: 'Include ARIA attributes, screen reader compatibility, and customizable focus indicators to meet accessibility standards.',
        },
        {
          title: 'Advanced Conditional Logic',
          text: 'Create logic flows that dynamically show or hide elements, based on user actions or inputs.',
        },
        {
          title: 'Auto Dark Mode Version',
          text: 'Automatically generate a dark mode version of your design, adapting colors, fonts, and backgrounds to suit a dark theme without extra effort.',
        },
        {
          title: 'Custom Dark Mode Version',
          text: 'Gain full control to design a custom dark mode version, allowing you to fine-tune colors, visuals, and styles independently of the light mode.',
        },
        {
          title: 'User Toggle for Dark Mode',
          text: 'Add a floating toggle button that lets users switch between light and dark modes seamlessly.',
        },
        {
          title: 'Dark Mode by URL Parameter',
          text: 'Enable dark mode through a specific URL parameter (e.g., ?darkmode=true), making it easy to share pre-set experiences.',
        },
      ],
    },
    SCEditorCopy: {
      title: 'Let’s meet Storycards Editor',
      embed_1: {
        title: 'video design on on the editor ',
        info: [
          {
            title: 'Design as you want ',
            text: `Absolute control and freedom to design just the way you want it. Start with a template or from scratch and build your custom design. Smooth drag and drop, no coding required. `,
          },
          {
            title: 'Design as you want ',
            text: `Absolute control and freedom to design just the way you want it. Start with a template or from scratch and build your custom design. Smooth drag and drop, no coding required.`,
          },
          {
            title: 'Design as you want ',
            text: `Absolute control and freedom to design just the way you want it. Start with a template or from scratch and build your custom design. Smooth drag and drop, no coding required.`,
          },
          {
            title: 'Design as you want ',
            text: `Absolute control and freedom to design just the way you want it. Start with a template or from scratch and build your custom design. Smooth drag and drop, no coding required.`,
          },
          {
            title: 'Design as you want ',
            text: `Absolute control and freedom to design just the way you want it. Start with a template or from scratch and build your custom design. Smooth drag and drop, no coding required.`,
          },
        ],
      },
    },
    collaboration: {
      title: 'Storycards platform is great to work on together with your team.',
      pageInfo: {
        title:
          'Build your organization, invite your team, and collaborate effortlessly with full transparency and control.',
        text: `Create your organization, add team members, and invite collaborators to seamlessly work together on your story. Instantly track changes and identify who made updates to ensure transparency and accountability.\n\nStreamline teamwork with advanced roles and permissions. Organize your teams into groups, each managed by a group leader. Group members can access only the products relevant to their group, while users can be assigned to multiple groups for cross-functional collaboration.\n\nTake full control with a robust permissions system. Define exactly what each user can do—whether it’s viewing analytics and reports, making edits, or creating new products. Tailor permissions to your needs and foster a secure, efficient workflow.`,
      },
      infoSection: [
        {
          title: 'Real-time team collaboration',
          text: 'Streamline teamwork with real-time collaboration. See who is editing your story, track changes instantly, and ensure seamless workflows.',
        },
        {
          title: 'Advanced roles and permissions',
          text: 'Manage your organization effortlessly with advanced role-based access controls. Assign permissions for editing, analytics, and product creation to ensure a secure and efficient workflow.',
        },
        {
          title: 'Group-based access control',
          text: 'Divide your organization into groups or departments, allowing teams to focus only on their own products and data. Assign managers to oversee groups and ensure complete separation where needed.',
        },
        {
          title: 'Unlimited revision history',
          text: 'Track every change made to your products. Access a detailed revision history to review or restore previous versions at any time.',
        },
        {
          title: 'Analytics-only access',
          text: 'Grant analytics permissions to data specialists, allowing them to view dashboards and reports without accessing or editing other product details.',
        },
        {
          title: 'Shared product libraries',
          text: 'Create and access a centralized library of templates and reusable products. Save time and maintain quality across all projects.',
        },
        {
          title: 'Integrated workflows across teams',
          text: 'Enable collaboration between marketing, product, and data teams. Build digital products effortlessly while ensuring consistency and alignment across departments.',
        },
        {
          title: 'Multi-site management',
          text: 'Oversee multiple sites under a single organization. Assign unique permissions and manage content independently for each site.',
        },
        {
          title: 'Force two-factor authentication',
          text: 'Enhance security by requiring two-factor authentication for all team members, ensuring your organization remains protected.',
        },
        {
          title: 'Group segmentation',
          text: 'Easily segment your organization into teams with custom dashboards and separate workflows for each group. Assign users to multiple groups for cross-department collaboration.',
        },
        {
          title: 'Seamless designer-editor workflow',
          text: 'Empower designers to create templates and content editors to build products. Maintain control with permissions that limit editing to approved areas.',
        },
        {
          title: 'Organizational template gallery',
          text: 'Browse through a curated gallery of templates tailored for your organization. Select a template and quickly create new products with just a few clicks, saving time and ensuring consistency.',
        },
        {
          title: 'Virtual collaboration academy',
          text: 'Access exclusive virtual training sessions to enhance your team’s collaboration skills and maximize productivity on Storycards.',
        },
        {
          title: 'Priority support for teams',
          text: 'Benefit from 24/7 email and phone support with priority response SLAs, ensuring your team stays productive and on track.',
        },
        {
          title: 'Organization-wide analytics',
          text: 'Track performance across your entire organization with unified analytics dashboards. Gain insights into team performance and product success.',
        },
        {
          title: 'Customizable user interfaces',
          text: 'Tailor the Storycards workspace to your team’s specific needs. Simplify workflows and ensure that every user has access to the tools they need.',
        },
        {
          title: 'Integrated data transfer',
          text: 'Ensure compliance and security with real-time data transfers to your CRM or database. Maintain control over your information while simplifying collaboration.',
        },
        {
          title: 'Custom training and onboarding',
          text: 'Schedule personalized virtual training sessions to onboard new team members and help them master Storycards.',
        },
        {
          title: 'Enhanced security for enterprises',
          text: 'Keep your organization secure with enterprise-grade tools such as two-factor authentication, advanced permissions, and user activity tracking.',
        },
        {
          title: 'Fast product search',
          text: 'Quickly locate products across your organization with a robust search function, saving time and improving workflow efficiency.',
        },
      ],
    },
    dataAndPrivacy: {
      topSection: {
        title: 'Your Data, Your Control. Secure and Flexible!',
        subTitle:
          'Storycards gives you the flexibility to decide how to manage your user data. Streamline your workflow by transferring data directly to your CRM or database.',
      },
      subTitle: 'The Storycards analytics dashboard helps you track and understand your audience’s engagement levels.',
      subTitleDescription: `Storycards analytics provides a complete breakdown of your data, tracking performance by stories and cards. Analyze user behavior, their choices, completion rates, and lead submissions to uncover what resonates with your audience.`,
      infoSection: [
        {
          title: 'Data Encryption',
          text: `Your users' data is securely encrypted, and only those with granted permissions can view or export it.`,
        },
        {
          title: 'Export User Data',
          text: 'Easily download all user data for each story you’ve created, including leads and user responses. Effortlessly export this data to any third-party CRM.',
        },
        {
          title: 'Data Integration',
          text: 'Directly send user leads and behavior data to your CRM or data warehouse to enrich your existing user profiles. Maximize the value of the Storycards platform by seamlessly integrating it with your current customer management and marketing tools.',
        },
        {
          title: 'Data Bridge',
          text: 'Your data remains yours. With Data Bridge, transfer data directly to your servers or databases without storing it on the Storycards cloud, ensuring full control and compliance.',
        },
        {
          title: 'Data Management',
          text: 'You have full control over your data. Delete information for any story at any time from your dashboard, giving you the flexibility to manage it your way.',
        },
        {
          title: 'Device and platform analysis',
          text: 'Understand how users interact with your stories across devices and platforms. Optimize your design for mobile, desktop, or tablet to ensure seamless engagement everywhere.',
        },
        {
          title: 'Geographic performance',
          text: `Track where your audience is located and understand engagement levels by region. Use these insights to tailor your content to specific locations.`,
        },
        {
          title: 'Completion rate analytics',
          text: 'See how many users finish your stories and what might be causing drop-offs. Use this data to refine your storytelling for better engagement.',
        },
        {
          title: 'User journey mapping',
          text: 'Visualize how users navigate through your stories. Identify common paths, bottlenecks, and opportunities for improvement.',
        },
        {
          title: 'Exportable reports',
          text: 'Generate and download detailed analytics reports to share with your team or stakeholders. Get insights in a clear, actionable format.',
        },
      ],
    },
    flow: {
      topSection: {
        title: 'Tailor every experience with smart flows',
        subTitle: 'Create dynamic flows that deliver personalized, seamless experiences every step of the way.',
      },
      subtitle:
        "Design multiple flows to deliver unique, personalized experiences tailored to each user's journey on every card.",
      description:
        'Storycards gives content editors the tools to present customized content and card types for every user, ensuring the experience evolves based on their interactions and engagement.\n\nThe intuitive Smart Flow interface puts you in control of the user journey. Build unlimited cards and effortlessly guide users along tailored paths, responding to their specific answers or actions.',
      scrollSection: [
        {
          title: 'Personalize with Dynamic Fields',
          desc: '"Hi, $firstname!" Use Dynamic Content to personalize experiences with details the respondent has provided, such as their name, job or other information gathered in the flow.',
          videoSrc: {
            desktop: 'https://assts.stories.sc/website/desktop/get_personal.mp4',
            mobile: 'https://assts.stories.sc/website/mobile/get_personal.mp4',
          },
          poster: { desktop: 'https://assts.stories.sc/website/poster/get_personal.jpg' },
        },
        {
          title: 'Scoring-Driven Navigation',
          desc: 'Use scoring for answers and questions to create personalized user experiences. Tailor results and content based on the points users accumulate, delivering a unique and dynamic journey.',
          videoSrc: {
            desktop: 'https://assts.stories.sc/website/desktop/scoring.mp4',
            mobile: 'https://assts.stories.sc/website/mobile/scoring.mp4',
          },
          poster: { desktop: 'https://assts.stories.sc/website/poster/scoring.jpg' },
        },
        {
          title: 'Enhance Journeys with URL Parameters',
          desc: 'Greet users by name right from the start without requiring them to fill in any details. Use URL parameters to pass data like names, IDs, or emails, enabling personalized user experiences that boost conversion rates.',
          videoSrc: {
            desktop: 'https://assts.stories.sc/website/desktop/url_parameters.mp4',
            mobile: 'https://assts.stories.sc/website/mobile/url_parameters.mp4',
          },
          poster: { desktop: 'https://assts.stories.sc/website/poster/url_parameters.jpg' },
        },
        {
          title: 'Personalized Flows with Conditional Logic',
          desc: `Create multiple flows and tailor unique experiences based on users' choices. Make your flow smarter by letting it react dynamically to users’ answers.`,
          videoSrc: {
            desktop: 'https://assts.stories.sc/website/desktop/flowcondtion.mp4',
            mobile: 'https://assts.stories.sc/website/mobile/flowcondtion.mp4',
          },
          poster: { desktop: 'https://assts.stories.sc/website/poster/flowcondtion.jpg' },
        },
      ],
      scrollSectionTitle: 'Personalize Journeys for Each User',
      scrollSectionSubTitle:
        'Storycards empowers you to craft digital experiences tailored to each user, creating personal journeys that maximize impact and success.',
      infoSection: [
        {
          title: 'Dynamic flow customization',
          text: `Create personalized user journeys with tailored content and seamless navigation, ensuring every interaction feels unique and engaging.`,
        },
        {
          title: 'Smart scoring and tailored outcomes',
          text: `Assign scores to answers and deliver personalized results, adjusting content dynamically based on user performance and preferences.`,
        },
        {
          title: 'Effortless conditional logic',
          text: `Design multiple paths that adapt to user choices in real-time, ensuring a custom experience for each participant.`,
        },
        {
          title: 'Real-time personalization with dynamic fields',
          text: `Use user-provided details like names or roles to create engaging, personal experiences, such as, “Hi, $firstname!”`,
        },
        {
          title: 'Seamless personalization with URL parameters',
          text: `Pass user data like names or IDs through URLs to create personalized experiences from the first interaction, boosting engagement and conversion rates.`,
        },
        {
          title: 'Interactive engagement cards',
          text: `Incorporate quizzes, polls, yes/no questions, and lead forms to create a diverse and captivating engagement journey.`,
        },
        {
          title: 'Adaptive user navigation',
          text: `Use answers and scores to dynamically navigate users to relevant content and outcomes, creating meaningful experiences for every participant.`,
        },
        {
          title: 'Repetition control for participation',
          text: `Set your product to allow one-time interactions or unlimited participation, tailoring the experience to your goals.`,
        },
        {
          title: 'Flexible form customization',
          text: `Design forms with fields of your choice, set mandatory fields, and include validations to ensure accurate data collection.`,
        },
        {
          title: 'User-driven result sharing',
          text: `Empower users to share their results directly from your FLOW, boosting virality and driving engagement.`,
        },
        {
          title: 'Unlimited card creation',
          text: `Build an infinite number of cards to design rich, multi-step flows that keep users engaged from start to finish.`,
        },
        {
          title: 'Multi-device optimization',
          text: `Ensure flawless experiences across mobile, tablet, and desktop, adapting every flow to the user’s device for maximum impact.`,
        },
        {
          title: 'A/B testing integration',
          text: `Experiment with different FLOW designs to identify the best-performing structure and optimize user engagement.`,
        },
        {
          title: 'Track and refine user journeys',
          text: `Analyze drop-off points and user interactions to refine your flow, improving engagement and retention.`,
        },
        {
          title: 'Engagement logic insights',
          text: `Use a smart scoring system to uncover patterns in user behavior and adjust your flow for better results.`,
        },
        {
          title: 'Dynamic event tracking',
          text: `Monitor specific user actions, like clicks and form submissions, to gain actionable insights into their engagement patterns.`,
        },
        {
          title: 'Data-driven optimization tools',
          text: `Leverage real-time analytics to refine and optimize your FLOW, maximizing impact and conversion rates.`,
        },
        {
          title: 'Customized follow-ups',
          text: `Send tailored follow-ups based on user responses and interactions, keeping them engaged even after they leave the FLOW.`,
        },
        {
          title: 'Enhanced user interaction design',
          text: `Combine multiple product types into a single flow, delivering a cohesive and engaging user experience.`,
        },
        {
          title: 'Scoring-based content display',
          text: `Show different cards or content based on accumulated points, creating a dynamic and personalized user journey.`,
        },
      ],
    },
    pricing: {
      title: 'Choose the plan that works for you',
      plans: [
        // {
        // 	name: 'Starter',
        // 	price: '$99',
        // 	billing: 'Billed monthly',
        // 	cta: 'Get Started',
        // 	views: 'Up to 10k page views',
        // 	features: [
        // 		'1 Seat',
        // 		'<p>Create up to 5 stories</p>',
        // 		'Variety of engagement products',
        // 		'Storycards Editor',
        // 		'Design by platform and devices',
        // 		'Build multiple flows',
        // 		'Smart Engagement Logics',
        // 		'Lead generation system',
        // 		'Scoring system',
        // 		'Rich animation',
        // 		'Templates gallery',
        // 		'Real Time Analytics dashboard',
        // 		'Export users data',
        // 		'SEO tools',
        // 		'Embed anywhere',
        // 		'Custom URL',
        // 		'Unlimited revision history',
        // 	],
        // },
        {
          name: 'Professional',
          price: '$499',
          billing: 'Billed monthly',
          cta: 'Get Started',
          views: 'Up to 100K page views',
          features: [
            '10 seats',
            'Create up to 20 stories',
            '<span>Up to 3 teams</span>',
            'Variety of engagement products',
            'Storycards Editor',
            'Design by platform and devices',
            'Build multiple flows',
            'Smart Engagement Logics',
            'Lead generation system',
            'Scoring system',
            'Rich animation',
            'Templates gallery',
            'Real Time Analytics dashboard',
            'Export users data',
            'SEO tools',
            'Embed anywhere',
            'Custom URL',
            'Unlimited revision history',
            'Create your organization',
            'Organization Analytics dashboard',
            'Advanced roles and permissions',
            '24/5 Email Support',
            '99.9% uptime',
          ],
        },
        {
          name: 'Agencies',
          price: '$1999',
          billing: 'Billed monthly',
          cta: 'Get Started',
          views: 'Up to 500K page views',
          features: [
            '30 Seats',
            'Create up to 100 stories',
            '<span>Up to 10 teams</span>',
            'Everything in Professional+',
            'Custom Domain',
            'Custom CSS',
            'Marketing pixels',
            '24/5 Email Support',
            '99.9% uptime',
          ],
        },
        {
          name: 'Enterprise',
          price: 'Custom',
          billing: '',
          cta: 'Contact us',
          views: 'Unlimited page views',
          features: [
            'Unlimited Seats',
            'Unlimited Stories',
            '<span>Unlimited teams</span>',
            'Everything in Agencies+',
            'Custom fonts',
            'Add your own code and scripts',
            'Google Analytics Integration',
            'Add your custom tracker',
            'Dedicated 3rd-party Integration',
            '24/7 email and phone support',
            'Response time SLA',
            'Dedicated account manager',
            'Advanced production services',
            'Virtual training with a professional Storycards designer',
            '99.9% uptime',
          ],
        },
      ],
      warranty: [
        { title: 'Accepted payment methods', description: null },
        {
          title: 'Money back guarantee',
          description: "Try Storycards for 14 days and if you're not 100% satisfied, get your money back.",
        },
        {
          title: 'SSL secure payment',
          description: 'Your information is protected by SSL encryption.',
        },
      ],
    },
    about: {
      topSection: {
        title: 'Meet Our Story',
        subTitle:
          'Storycards is an AI-powered no-code engagement platform built for enterprises.  We empower organizations to create fully branded, interactive digital experiences that captivate users and collect data – all without writing a single line of code. Every month, Storycards-powered experiences engage over 10 million people worldwide, turning ordinary content into measurable, memorable interactions.',
      },
      texts: [
        {
          title: 'Our Journey',
          paragrahs: [
            {
              text: 'Storycards was born out of years of hands-on experience building digital products that keep users engaged, interacting, and converting. Founded in 2005 as a leading UI/UX and technology development company, our team spent over a decade creating large-scale digital experiences for some of the world’s biggest organizations — media companies, telecoms, banks, and global brands.',
            },
            {
              text: 'Throughout hundreds of projects, one challenge kept repeating itself: how to design experiences that make people stay longer, interact more, and share valuable data — all while keeping the brand consistent and the experience seamless. We became experts at solving that problem — combining behavioral design, technology, and data to maximize user engagement and conversion.',
            },
            {
              text: 'By 2018, we noticed a clear pattern: we were rebuilding the same engagement mechanics from scratch for every client. So we decided to change the game. Instead of reinventing the wheel, we created an internal platform to automate and scale interactive experience creation — empowering our clients to launch faster while maintaining creativity, security, and full control over their data.',
            },
            {
              text: 'The impact was transformative: engagement rates soared, workflows became dramatically faster, and data collection became structured, measurable, and actionable. Realizing the potential, we evolved that internal technology into what is now Storycards — a self-service SaaS platform built hand-in-hand with leading organizations, capable of handling massive traffic, personalization, and enterprise-level integrations.',
            },
            {
              text: 'From a pioneering development company in 2005 to a global no-code platform today, our journey has always been driven by one mission: helping organizations turn user engagement into real, measurable business value.',
            },
          ],
        },
        {
          title: 'Our Vision',
          paragrahs: [
            {
              text: 'Our mission is to redefine how enterprises create and optimize interactive experiences. While most no-code platforms stop at building, Storycards goes further — combining creation, performance optimization, and AI-driven intelligence into one seamless ecosystem.',
            },
            {
              text: 'We believe that every organization — from global corporations to in-house marketing teams — should be able not only to create engaging digital products but also to continuously improve them, automatically, without any extra effort.',
            },
            {
              text: 'Behind the scenes, Storycards’ AI constantly analyzes performance data — tracking engagement rates, drop-offs, and user behavior — and then applies smart optimizations in real time. From adjusting layouts and interaction flow to refining content hierarchy and timing, our platform intelligently learns what works best for your audience and enhances it — all without you lifting a finger.',
            },
            {
              text: 'This means every experience you launch doesn’t just stay static — it evolves. It gets smarter, faster, and more effective over time. By merging deep engagement science with adaptive AI, Storycards helps organizations turn creativity into measurable performance and data-driven growth.',
            },
            {
              text: 'Our vision is to be the go-to platform for enterprise engagement and optimization — where creating, analyzing, and improving experiences happens in one place, automatically.We’re building a future where interactive, personalized digital experiences are not only effortless to create — but also self-optimizing by design.',
            },
          ],
        },
        {
          title: 'What We Do',
          paragrahs: [
            {
              text: 'Today, Storycards stands at the forefront of enterprise-grade no-code engagement. In a landscape where traditional website builders and form tools fall short, Storycards provides a dedicated solution for interactive marketing, onboarding, and data collection.',
            },
            {
              text: 'Our platform blends the creative power of generative AI with the precision of human design. Start with a simple prompt or idea, and Storycards’ AI will generate layouts, content suggestions, and design elements aligned with your brand. Then, refine every detail through our intuitive drag-and-drop studio — achieving pixel-perfect experiences in hours, not weeks.',
            },
            {
              text: 'Every user interaction – every click, answer, or choice – is automatically captured as structured, analytics-ready data. Unlike many platforms, Storycards ensures you maintain full ownership of your data. Responses are sent securely and in real time to your systems, respecting user privacy and enterprise-grade compliance. Our APIs and integrations make it easy to connect with your existing stack — from CRMs to marketing automation.',
            },
            {
              text: 'Organizations choose Storycards because it offers the creativity of an agency with the reliability of enterprise software. With features like version control, team roles, and brand libraries, teams can collaborate at scale while maintaining compliance and brand consistency.',
            },
          ],
        },
      ],
      ceo: {
        name: 'Gil Rabbi',
        title: 'Meet the Founder',
        text: "<a href='https://storycards.com/gil-rabbi' target='_blank' id='gilLink'>Gil Rabbi</a> is a recognized expert in digital engagement and the visionary behind Storycards. With over two decades of experience developing interactive products that reach millions of users, Gil has shaped how audiences connect with digital content. <br/><br/> Before founding Storycards, he led award-winning initiatives for leading media companies and global publishers. His deep understanding of what captures attention and sustains engagement became the foundation of a new kind of no-code platform — one designed to deliver measurable results at enterprise scale. <br/><br/> <i>Rabbi was named among Israel’s 40 Under Forty and the Top 100 Most Influential Digital Leaders.</i>",
        description:
          "Rabbi was named in the 40 under Forty list of the most influential people in Israel and as one of 100 most influential people in the Israeli digital world. Rabbi's main specialty over the last decade has been how to make users stay longer on sites and apps, making them more involved in content.",
      },
    },
    pressCenter: {
      title: 'Press Center',
      news: [
        {
          website: 'Authority Magazine',
          title: 'Gil Rabbi Of Storycards On How Their Technological Innovation Will Shake Up The Tech Scene',
          description:
            'As a part of our series about cutting-edge technological breakthroughs, I had the pleasure of interviewing Gil Rabbi.',
          link: 'https://medium.com/authority-magazine/the-future-is-now-gil-rabbi-of-storycards-on-how-their-technological-innovation-will-shake-up-the-39422f9ff920',
          date: '22/12/21',
        },
        {
          website: 'European Business Review',
          title: '5 Innovative Startups To Watch',
          description:
            'In this article, we will present five startups that you should know about. These startups are innovating their industries and are providing lots of value to their partners and clients.',
          link: 'https://www.europeanbusinessreview.com/5-innovative-startups-to-watch/',
          date: '10/01/22',
        },
        {
          website: 'Latinpost',
          title: 'No-Code - Telling The Gil Rabbi Story',
          description:
            'Storycards is a game-changing innovation for eCommerce retailers and for digital agencies. They provide the opportunity to create products without the need for any costly development.',
          link: 'https://www.latinpost.com/articles/153583/20220113/no-code-telling-the-gill-rabbi-story.htm',
          date: '13/01/22',
        },
        {
          website: 'Techbullion',
          title: 'Storycards is Helping Businesses Stay in the Limelight',
          description:
            '“One of the keys to the effectiveness of Storycards is the way that it employs artificial intelligence,” Rabbi explains. “The platform provides feedback on how to improve itself as well as how to increase the level of engagement with the site users. ',
          link: 'https://techbullion.com/in-a-world-where-visual-quality-is-paramount-storycards-is-helping-businesses-stay-in-the-limelight/',
          date: '5/01/22',
        },
        {
          website: 'EuroWeekly',
          title: 'Israeli Tech Mogul Gil Rabbi on How Storycards Engages the Internet',
          description: `These tools put business users in the driver’s seat of custom application development projects with little or no coding skills required.`,
          link: 'https://www.euroweeklynews.com/2021/12/20/israeli-tech-mogul-gil-rabbi-on-how-storycards-engages-the-internet/',
          date: '20/12/21',
        },
        {
          website: 'CTECH',
          title: 'As Google bans third-party cookies, publishers find new ways to gather data',
          description:
            'Storycards is an Israeli company giving power back to publishers who are expecting to lose their advertising methods next year.',
          link: 'https://www.calcalistech.com/ctech/articles/0,7340,L-3927138,00.html',
          date: '13/01/22',
        },
        {
          website: 'itechpost',
          title: "The Future is Here - Gil Rabbi's 'No-Code'",
          description: `Engagement products are a new breed of no-code platforms that focus on enabling marketers to build platforms for customers and prospects-or "customer engagement" if you will.`,
          link: 'http://www.itechpost.com/articles/108686/20220113/the-future-is-here-gil-rabbis-no-code.htm',
          date: '13/01/22',
        },
        {
          website: 'International Business Times UK',
          title: 'Not just Wix: Another Israeli Technology Enables No Coding Engagement Products',
          description:
            'Following the huge success of Wix in no-coding web design, another Israeli company, Storycards, led by engagement guru Gil Rabbi will bring no-coding to online customer engagement.',
          link: 'https://www.ibtimes.co.uk/not-just-wix-another-israeli-technology-enables-no-coding-engagement-products-1695676',
          date: '22/10/21',
        },
        {
          website: 'Entrepreneur',
          title: 'With Most Consumers Online, The No Code Market Is Heating Up',
          description:
            'One such solution is Storycards, an Israeli startup founded by Gil Rabbi. As an enabler of freedom design, Storycards gives users the ability to create any concept and freedom design as they desire.',
          link: 'https://www.entrepreneur.com/article/393233',
          date: '25/10/21',
        },
        {
          website: 'Finance Yahoo',
          title: 'Storycards Has Built a No-Code Platform For Engagement Products',
          description:
            'Storycards has been chosen by The European Business Review as one of 5 innovative startups to watch.',
          link: 'https://finance.yahoo.com/news/storycards-built-no-code-platform-114200621.html',
          date: '12/01/22',
        },
        {
          website: 'World Israel News',
          title: 'How Israeli startups are transforming their industries',
          description:
            'Israel has a flourishing startup ecosystem, which provides a fertile ground for many of the world’s most innovative technologies. It is home to the largest number of startups outside of Silicon Valley and is ranked as one of the most innovative countries in the world.',
          link: 'https://worldisraelnews.com/these-israeli-startups-are-transforming-their-industries/',
          date: '21/12/21',
        },
        {
          website: 'Times of Israel',
          title: 'Tech Leaders: Gil Rabbi, Founder, Storycards',
          description: `Interview is with Gil Rabbi, Founder & CEO of Storycards.`,
          link: 'https://blogs.timesofisrael.com/tech-leaders-gil-rabbi-founder-storycards/',
          date: '12/12/21',
        },
        {
          website: 'Newsroom',
          title: 'Israeli Digital Pioneer Gil Rabbi talks about the inspiration behind Storycards',
          description:
            'It is the Internet’s first no-code engagement product that can change the user experience for people around the globe.',
          link: 'https://newsroompost.com/tech/israeli-digital-pioneer-gil-rabbi-talks-about-the-inspiration-behind-storycards/5049035.html',
          date: '20/12/21',
        },
        {
          website: 'Influencive',
          title: 'MEET GIL RABBI – ONE OF ISRAEL’S DIGITAL PIONEERS',
          description:
            'His passion and astuteness in technology led him to push innovation in the digital world, founding “Storycards” and “Rabbi Interactive Agency.',
          link: 'https://www.influencive.com/meet-gil-rabbi-one-of-israels-digital-pioneers',
          date: '08/02/22',
        },
        {
          website: 'Business News Ledger',
          title: 'The Future Of Engagement Products With Gil Rabbi: No Coding Website Development',
          description:
            'Storycards, a startup based in Israel, aims to revolutionise the way we look at and use no coding website development services.',
          link: 'https://businessnewsledger.com/the-future-of-engagement-products-with-gil-rabbi-no-coding-website-development/',
          date: '06/11/21',
        },
        {
          website: 'Israel National News',
          title: 'The Israeli engagement expert shares his knowledge with everyone, using a new technology',
          description:
            'Israeli engagement expert Gil Rabi announced another new innovation in the area of online engagement.',
          link: 'https://www.israelnationalnews.com/news/316416',
          date: '07/11/21',
        },
        {
          website: 'Apn News',
          title: 'Gil Rabbi’s Storycards garners him headlines across the world',
          description:
            'As the Founder of Storycards and Rabbi Interactive Agency, he has proved what he truly possesses as Israel’s digital pioneer.',
          link: 'https://www.apnnews.com/gil-rabbis-storycards-garners-him-headlines-across-the-world/',
          date: '30/01/22',
        },
        {
          website: 'disruptmagazine',
          title: '“Storycards” by Gil Rabbi, the engagement guru at a global level.',
          description:
            'A quick look around us will let us know how a few people, brands, and businesses have been making noise for the kind of work and the level of contribution they are making in their respective industries.',
          link: 'https://disruptmagazine.com/make-way-for-the-new-no-code-engagement-platform-storycards-by-gil-rabbi-the-engagement-guru-at-a-global-level/',
          date: '30/01/22',
        },
        {
          website: 'News Heads',
          title: `Storycards, a no-code engagement platform by Gil Rabbi`,
          description:
            'Here is a reason he is known as the engagement guru and digital pioneer of Israel, thanks to his tenacity and passion for creating digital products and improving engagement.',
          link: 'https://www.newsheads.in/tech/news/storycards-a-no-code-engagement-platform-by-gil-rabbi-has-thrust-him-forward-in-the-digital-world-article-67099',
          date: '12/01/22',
        },
        {
          website: 'Inventiva',
          title: 'The Engagement Expert Gil Rabbi Reveals What Makes Storycards Unique',
          description:
            'Everyone believes that you need coding knowledge to create a good website that will engage users, however, Israeli tech guru Gil Rabbi has changed these notions.',
          link: 'https://www.inventiva.co.in/trends/inventiva/the-engagement-expert-gil-rabbi-reveals-what-makes-storycards-unique/',
          date: '22/12/21',
        },
        {
          website: 'Newsbreak',
          title: 'Startups To Watch: Simpool, Storycards, SubStrata, AllSeated, Synk',
          description: `The platform’s workflow lets editors develop great interactive experiences, mix and match from various products to create a unique engagement and customize behavior experience according to their needs.
          `,
          link: 'https://original.newsbreak.com/@omri-hurwitz-1589763/2461825986589-startups-to-watch-simpool-storycards-substrata-allseated-synk',
          date: '15/12/21',
        },
        {
          website: 'News Track Live',
          title: 'Tech Superstar Gil Rabbi Of Storycards On How He Is Making The Best Of Artificial Intelligence',
          description:
            'Storycards assign a score for each product created on the platform, which shows the website or app owner what users are doing with it and what should be improved, in real-time.',
          link: 'https://english.newstracklive.com/news/tech-superstar-gil-rabbi-of-storycards-on-how-he-is-making-the-best-of-artificial-intelligence-sc71-nu328-ta328-1205434-1.html',
          date: '15/01/22',
        },
        {
          website: 'Ritz Herald',
          title: 'Gil Rabbi will bring no-coding to the user engagement field',
          description:
            'The huge success of companies that give the option to create websites without knowledge, Storycards, led by one of Israel’s digital pioneers, Gil Rabbi, is making a bold attempt to do the same but in the world of engagement.',
          link: 'https://ritzherald.com/gil-rabbi-will-bring-no-coding-to-the-user-engagement-field/',
          date: '05/11/21',
        },
        {
          website: 'techtribune',
          title:
            'Alors que Google interdit les cookies tiers, les éditeurs trouvent de nouvelles façons decollecter des données',
          description: `C’est un truc de fou, ça va changer tout le secteur publicitaire”, a expliqué Gil Rabbi, fondateur et PDG de Storycards.
          `,
          link: 'https://fr.techtribune.net/google/alors-que-google-interdit-les-cookies-tiers-les-editeurs-trouvent-de-nouvelles-facons-de-collecter-des-donnees/220726/',
          date: '20/01/22',
        },
        {
          website: 'The Science Times',
          title: "The Revolutionary 'No-Code' Era - The Gil Rabbi Story",
          description: `A massive market for lower-tech software is emerging as an increasing number of companies create engagement products powered by a new breed of low-code platforms.`,
          link: 'https://www.sciencetimes.com/articles/35547/20220113/the-revolutionary-no-code-era-the-gil-rabbi-story.htm',
          date: '13/01/22',
        },
        {
          website: 'Entrepreneurs Break',
          title: 'Israeli Tech Mogul Gil Rabbi on How Storycards Engages the Internet',
          description:
            'The company hopes that its invention, the internet’s first no-code engagement product, will deliver next-level engagement and user experience while fostering a new development culture.',
          link: 'https://entrepreneursbreak.com/israeli-tech-mogul-gil-rabbi-on-how-storycards-engages-the-internet.html',
          date: '9/12/21',
        },
        {
          website: 'Digital Journal',
          title: 'Storycards Has Built a No-Code Platform For Engagement Products',
          description:
            'Storycards, the world’s first editor to build professional and custom engagement products in a completely visual canvas with no-code, has been chosen by The European Business Review as one of 5 innovative startups to watch.',
          link: 'https://www.digitaljournal.com/pr/storycards-has-built-a-no-code-platform-for-engagement-products',
          date: '13/01/22',
        },
        {
          website: 'The Daily Scanner',
          title: 'Gil Rabbi – Storycards Is The New Rising Star Platform In The Digital World',
          description:
            'Guru Gil Rabbi is one of the finest coders in the industry, leading to Israeli Storycards, one of the most exciting companies after the success of Wix. The increase of Wix has led to more and more users capable of creating their own websites without any need for knowledge.',
          link: 'https://www.dailyscanner.com/gil-rabbi-storycards-is-the-new-rising-star-platform-the-digital-world/',
          date: '05/11/21',
        },
        {
          website: 'Hudson Weekly',
          title: 'Gil Rabbi Will Bring No-Coding to the User Engagement Field',
          description:
            'With the huge success of companies that give the option to create websites without knowledge, Storycards is making a bold attempt to do the same but in the world of engagement.',
          link: 'https://hudsonweekly.com/gil-rabbi-will-bring-no-coding-to-the-user-engagement-field/',
          date: '05/11/21',
        },
        {
          website: 'Kingstreetoyster',
          title: 'Israeli Tech Mogul Gil Rabbi on How Storycards Engages the Net',
          description:
            'What can make Storycards get the job done with no coding is a nascent technology: AI. Storycards recommended how to increase the level of engagement with the consumers.',
          link: 'https://kingstreetoyster.com/israeli-tech-mogul-gil-rabbi-on-how-storycards-engages-the-net.html',
          date: '09/12/21',
        },

        {
          website: 'Net News Ledger',
          title: 'Gil Rabbi makes Garnering Consumer Attention Easier Than Ever',
          description: `Storycards, an interactive AI digital product aimed at capturing the attention of consumers and increasing interaction, while also providing real-time feedback regarding consumer behavior.`,
          link: 'https://www.netnewsledger.com/2022/02/02/gil-rabbi-makes-garnering-consumer-attention-easier-than-ever/',
          date: '02/02/22',
        },

        {
          website: 'ICE',
          title: `Keshet's partner will sell to others; The channel will separate from the shares and use the product for free`,
          description: `Keshet Broadcasting is cutting back on its holdings in startup Storycards, in light of the founder's expansion plans now turning to the international market.`,
          link: 'https://www.ice.co.il/digital-140/news/article/780989',
          date: '21/06/2020',
        },
      ],
    },
    gilRabbiPage: {
      gilRabbi: 'Gil Rabbi',
      founderAndSeo: 'Founder & CEO',
      firstTitle:
        'Gil Rabbi is a recognized expert in digital engagement and the founder of Storycards, the AI-powered no-code platform built for enterprises.',
      firstSubtitle:
        'For more than two decades, he has been creating interactive technologies that redefine how audiences connect, participate, and share data online. His products have reached over 10 million users every month, making him one of the most influential figures in engagement-based product innovation. <br /> <br /> He was twice recognized as one of Israel’s Top 100 Digital Leaders and appeared in the 40 Under Forty list of the country’s most influential business figures.',
      secondTitle:
        'How can technology make people stay longer, interact more deeply, and share meaningful data — all while enjoying the experience?',
      secondSubtitle:
        'Gil’s career has been defined by one central question: How can technology make people stay longer, interact more deeply, and share meaningful data — all while enjoying the experience? <br/><br/> Before founding Storycards, he led groundbreaking digital initiatives for global media companies and leading organizations, combining behavioral design, technology, and creativity to build experiences that merge storytelling with measurable performance. <br/><br/> <b>A Leader in Digital Engagement</b><br/> Gil’s professional journey began in the early 2000s, when he founded Rabbi Interactive, a technology company specializing in the development of high-scale interactive products and engagement platforms. <br/><br/> The company quickly became one of the region’s leading innovation hubs, creating real-time digital experiences for major broadcasters, telecom companies, and consumer brands. <br/><br/> Throughout the years, Gil and his team pioneered the use of <b>real-time participation technologies</b> — enabling millions of users to interact live with television, mobile, and digital content.<br/> Among their landmark projects was the <b>Rising Star</b> TV format, where viewers voted in real time during a live broadcast, choosing Israel’s representatives for the <b>Eurovision Song Contest</b>.<br/>This breakthrough technology was later adopted internationally and covered in an <b>Amazon Web Services (AWS) Case Study</b> for its advanced scalability and reliability.<br/><br/><b>Innovating the Future of Live Interaction</b><br/>Gil’s passion for large-scale engagement led him to develop technologies that connected audiences beyond traditional channels.<br/> In 2019, he introduced Crowdr, a patented system that activated over 600,000 mobile devices simultaneously during live events — without internet or cellular connectivity. Crowdr’s sound-based synchronization was used in over 120 large festivals and concerts, transforming spectators into active participants in the show.<br/><br/> Earlier, Gil led a groundbreaking digital campaign for Orange Mobile, where television commercials synchronized in real time with an online game — attracting hundreds of thousands of viewers to watch live just to participate.<br> He also developed interactive companion apps for shows like Touch, which allowed viewers to compete live with others during the broadcast. <br/>These products marked the evolution of second-screen technology — where the audience doesn’t just watch, but becomes part of the experience.<br/><br/><b>From Custom Development to Scalable Platforms</b><br/> After years of building custom engagement solutions, Gil noticed a recurring challenge: every organization wanted unique, interactive experiences — yet the development logic behind them was always similar. To solve this, he and his team began developing an internal platform to automate the creation of engagement-based digital products. <br/><br/> The success was immediate. <br/>The internal system allowed for rapid creation of on-brand, interactive experiences that collected data intelligently and scaled effortlessly under heavy traffic. <br/>That innovation became the foundation for Storycards — the no-code platform that now enables enterprises to build, launch, and automatically optimize engagement-driven digital products without writing a single line of code. <br/><br/> <b>Recognitions and Vision</b><br/> Gil has been featured in the 40 Under Forty list of influential business leaders and named among the Top 100 Most Influential Digital Leaders for his contributions to interactive product innovation. <br/> His work has influenced how global media and enterprises think about engagement, personalization, and real-time participation at scale. <br/><br/> Today, as the Founder and CEO of Storycards, Gil continues to lead innovation at the intersection of AI, no-code technology, and user behavior — helping organizations turn creativity and data into measurable, evolving digital performance.',
    },
    pageNotFound: {
      pageNotFound: 'Page not found',
      backToHome: 'Back to Home page',
    },
    startups: {
      title: 'Storycards for startups and brands',
      subtitle:
        'Generate better quality leads and improve their KPI results faster and focus on marketing strategy without spending money and resources on technology for marketing.',
      items: [
        {
          title: 'Save development resources',
          subtitle: 'Let your teams focus on marketing and free your development team for bigger challenges.',
        },
        {
          title: 'Premium results',
          subtitle:
            'Create better marketing products than you currently produce, get premium results, quicker and without using the development departments.',
        },
        {
          title: 'Stop wasting money on\n AB testing',
          subtitle:
            "AB testing doesn't need to cost a lot of money anymore, creates many marketing products easily, at no cost to market, and learns faster about your target audience.",
        },
        {
          title: 'Increase your leads',
          subtitle: 'Gather more leads for your products with interactive engagement tools.',
        },
        {
          title: 'Personalized segments',
          subtitle: 'Create smarter segments based on users’ choices and run personalized advertising campaigns.',
        },
        {
          title: 'Design by your brand guidelines',
          subtitle:
            ' Customize behavior according to your needs and design according to your brand guidelines so your product will look like it develops special for you.',
        },
        {
          title: 'Analytics and insights',
          subtitle:
            'Storycards analytics dashboard gives you all the information you need but you can always add or connect your tracker.',
        },
        {
          title: 'The data is always yours',
          subtitle: 'Get more value by connecting your existing CRM and marketing tools.',
        },
        {
          title: 'Encrypted data',
          subtitle: 'Your data is encrypted and only the users you have granted permission to access can view it.',
        },
        {
          title: 'Work with your team',
          subtitle: 'Drive collaboration with your team and create groups with advanced roles and permissions.',
        },
      ],
    },
    agencies: {
      title: 'Storycards for digital and advertising agencies',
      subtitle:
        'Make a hundred times more profit once they use Storycards. Bring back the power to the creative people so that they can create their ideas with zero cost.',
      items: [
        {
          title: 'Premium results',
          subtitle:
            'Create better products than you currently do for your clients, get premium results quicker, without the need for developers, and with zero cost.',
        },
        {
          title: 'Create your ideas at zero cost',
          subtitle:
            'Your customers do not have to imagine anymore, create the real product at zero cost and convince your customer to move forward with your idea.',
        },
        {
          title: 'Increase your leads',
          subtitle: 'Gather more leads for your customers with interactive engagement tools.',
        },
        {
          title: 'Personalized segments',
          subtitle: 'Create smarter segments based on users’ choices and run personalized advertising campaigns.',
        },
        {
          title: "Design by your customer's brand guidelines",
          subtitle:
            'Absolute control and freedom to design just the way you want it. Start from scratch and build custom designs for each client by Smooth drag and drop, with no coding.',
        },
        {
          title: 'Analytics and insights',
          subtitle:
            'Storycards analytics dashboard gives you all the information you need but you can always add or connect your tracker.',
        },
        {
          title: 'The data is always yours',
          subtitle: 'Get more value by connecting your existing CRM and marketing tools.',
        },
        {
          title: 'Encrypted data',
          subtitle: 'Your data is encrypted and only the users you have granted permission to access can view it.',
        },
        {
          title: 'Work with your team',
          subtitle: 'Drive collaboration with your team and create groups with advanced roles and permissions.',
        },
        {
          title: 'Publish and Scale',
          subtitle:
            'Go live with one click, and without any worries. Storycards network will scale your product by the number of visitors to your site.',
        },
      ],
    },
    publishers: {
      title: 'Storycards for publishers',
      subtitle:
        'Increase advertising revenue a hundred times more once they use Storycards and let your editors come up with great interactive experiences.',
      items: [
        {
          title: 'Bring the power\n to your editors',
          subtitle:
            'Let your editors come up with great interactive experiences and embed them on articles inside your site and mobile app.',
        },
        {
          title: 'Increase advertising revenue',
          subtitle: 'Sell specific and smarter segments that others cannot offer and increase advertising revenue.',
        },
        {
          title: 'Create premium articles',
          subtitle:
            'Create more interesting premium articles and show news content with interactive tools and at zero cost.',
        },
        {
          title: "Boost your users' engagement",
          subtitle:
            'Give the power to your users and make them involved in your content with polls and voting systems.',
        },
        {
          title: 'An integral part of\n your website',
          subtitle:
            'Storycards product will look like an integral part of the website or mobile app, as though it was custom developed for you.',
        },
        {
          title: 'Design by your site guidelines',
          subtitle:
            'Customize behavior according to your needs and design according to your brand guidelines so your product will look like it develops special for you.',
        },
        {
          title: 'Analytics and insights',
          subtitle:
            'Storycards analytics dashboard gives you all the information you need but you can always add or connect your tracker.',
        },
        {
          title: 'The data is always yours',
          subtitle: 'Get more value by connecting your existing CRM and marketing tools.',
        },
        {
          title: 'Encrypted data',
          subtitle: 'Your data is encrypted and only the users you have granted permission to access can view it.',
        },
        {
          title: 'Work with your team',
          subtitle: 'Drive collaboration with your team and create groups with advanced roles and permissions.',
        },
      ],
    },
    scrollingSection: {
      title: 'Create',
      subtitle:
        'Create premium engaging products with zero development costs. Mix and match from a variety of cards such as quiz, poll, lead form, and more to create a unique engagement experience.',
      items: [
        {
          title: 'Design With Freedom',
          text: 'Storycards gives you the ability to create any concept and the freedom to design as you want, with no limits. Cutting-edge responsive design with smooth drag and drop, no code needed.',
        },
        {
          title: 'Publish and Scale',
          text: 'Go live with one click, embed your product in your website or mobile app to increase engagement, or connect your domain for a comprehensive experience in full window mode.',
        },
        {
          title: 'Leads generation and Segments',
          text: 'Increase your leads, learn more about your users, and get real-time user data to find audience and content insights. Create smarter segments based on their choices and run personalized advertising campaigns.',
        },
      ],
    },
    ai: {
      topSection: {
        title: 'Your powerful AI engagement machine',
        subTitle: `Transform enterprise data collection with the power of Storycards' AI.`,
        subTitleMob: `Transform enterprise data collection with the power of Storycards' AI.`,
        btn: 'Start creating',
      },
      sections: {
        sectionTop: {
          title: `<p>Craft AI-powered products that captivate your audience, enhance user interaction, and drive exceptional results.<p>`,
        },
      },
      scrollSection: [
        {
          title: 'Paste your URL and see the magic happen',
          desc: "Storycards AI engine dives deep into your site's content and crafts a unique engagement product related to your content tailored perfectly to your site’s design guidelines.",
          videoSrc: {
            desktop: 'https://assts.stories.sc/website/desktop/ai_by_url.mp4',
            mobile: 'https://assts.stories.sc/website/mobile/ai_by_url.mp4',
          },
          poster: { desktop: 'https://assts.stories.sc/website/poster/ai_by_url.jpg' },
        },
        {
          title: 'Turn your prompt into a playful product',
          desc: 'Create customized content for your products automatically by entering your specific topic. Our AI engine automatically writes content, designs images, and creates the game logic.',
          videoSrc: {
            desktop: 'https://assts.stories.sc/website/desktop/aiplayful.mp4',
            mobile: 'https://assts.stories.sc/website/mobile/aiplayful.mp4',
          },
          poster: { desktop: 'https://assts.stories.sc/website/poster/aiplayful.jpg' },
        },
        {
          title: 'Gain better performance with Storycards AI',
          desc: `Storycards AI actively monitors user interactions with your products, continuously refining content and design to enhance engagement and conversion rates. This results in strengthened brand loyalty and increased revenue.`,
          videoSrc: {
            desktop:
              'https://player.vimeo.com/progressive_redirect/playback/912106329/rendition/720p/file.mp4?loc=external&log_user=0&signature=854c7a80f1452fe55a6d39e7da9f9dd4d1cbe9e730aac6567ea21c71821c108d',
          },
        },
        {
          title: 'Assign your design to our AI',
          desc: 'No more wasted hours on design – our AI automates it all. By detecting your brand’s design guidelines directly from your website, it crafts professional, visually stunning engagement products with precision.',
          videoSrc: {
            desktop:
              'https://player.vimeo.com/progressive_redirect/playback/912108327/rendition/720p/file.mp4?loc=external&log_user=0&signature=73153747d24f63e610db99527da12dcf02873f37e018e8ab0ec331aec6999214',
          },
        },
      ],
      bottomInfo: {
        paragraph1:
          'We all understand that creating engagement with our audience is the key to enhancing outcomes and data for any organization. However, finding the time to develop such engaging products, brainstorming the necessary content, and designing these items is often a challenge.',
        paragraph2:
          'Storycards enables you to create engaging premium products in just a few minutes. Imagine effortlessly crafting trivia, surveys, personality quizzes, games, and more, all designed to captivate and connect with your audience.',
      },
    },
    homepage: {
      create: {
        title: 'Create Anything. Collect Everything',
        subtitle: `Create any interactive experience designed to collect user information—whether it's onboarding flows, websites, registration processes, trivia, surveys, personality tests, smart forms and more.`,
        items: [
          {
            title: 'Find What Works, Faster',
            desc: 'Compare designs, layouts, and flows with smart A/B testing. Let data guide your decisions.',
            videoSrc: {
              desktop: 'https://assts.stories.sc/website/desktop/ab.mp4',
              mobile: 'https://assts.stories.sc/website/mobile/ab.mp4',
            },
            poster: { desktop: 'https://assts.stories.sc/website/poster/ab.jpg' },
          },
          {
            title: 'Real-Time insights and Drop-off',
            desc: 'Track engagement, identify drop-off points, and refine your flow with real-time insights. Analyze performance and optimize for better results.',
            videoSrc: {
              desktop: 'https://assts.stories.sc/website/desktop/insights.mp4',
              mobile: 'https://assts.stories.sc/website/mobile/insights.mp4',
            },
            poster: { desktop: 'https://assts.stories.sc/website/poster/insights.jpg' },
          },
          {
            title: 'Boost Conversions with Storycards AI',
            desc: 'Let Storycards handle A/B testing, data analysis, and flow optimization for you. Deliver tailored, high-performing pages to your visitors faster with AI-driven automation.',
            videoSrc: {
              desktop: 'https://assts.stories.sc/website/desktop/perfromance.mp4',
              mobile: 'https://assts.stories.sc/website/mobile/perfromance.mp4',
            },
            poster: { desktop: 'https://assts.stories.sc/website/poster/perfromance.jpg' },
          },
        ],
      },
      designContent: {
        tabsTitles: ['Design', 'Content'],
        tabsContent: [
          {
            title: 'Design as you want',
            text: 'Empowers designers to create any concept without limits. Cutting-edge responsive design with smooth drag-and-drop, no code needed.',
          },
          {
            title: 'Content your way',
            text: 'Let your team focus on content and create impactful products quickly, while preserving your brand’s identity and designer standards.',
          },
        ],
      },
      personalize: {
        title: 'Personalize Journeys for Each User',
        subtitle:
          'Storycards empowers you to craft digital experiences tailored to each user, creating personal journeys that maximize impact and success.',
        items: [
          {
            title: 'Personalize with Dynamic Fields',
            desc: '"Hi, $firstname!" Use Dynamic Content to personalize experiences with details the respondent has provided, such as their name, job or other information gathered in the flow.',
            videoSrc: {
              desktop: 'https://assts.stories.sc/website/desktop/get_personal.mp4',
              mobile: 'https://assts.stories.sc/website/mobile/get_personal.mp4',
            },
            poster: { desktop: 'https://assts.stories.sc/website/poster/get_personal.jpg' },
          },
          {
            title: 'Scoring-Driven Navigation',
            desc: 'Use scoring for answers and questions to create personalized user experiences. Tailor results and content based on the points users accumulate, delivering a unique and dynamic journey.',
            videoSrc: {
              desktop: 'https://assts.stories.sc/website/desktop/scoring.mp4',
              mobile: 'https://assts.stories.sc/website/mobile/scoring.mp4',
            },
            poster: { desktop: 'https://assts.stories.sc/website/poster/scoring.jpg' },
          },
          {
            title: 'Enhance Journeys with URL Parameters',
            desc: 'Greet users by name right from the start without requiring them to fill in any details. Use URL parameters to pass data like names, IDs, or emails, enabling personalized user experiences that boost conversion rates.',
            videoSrc: {
              desktop: 'https://assts.stories.sc/website/desktop/url_parameters.mp4',
              mobile: 'https://assts.stories.sc/website/mobile/url_parameters.mp4',
            },
            poster: { desktop: 'https://assts.stories.sc/website/poster/url_parameters.jpg' },
          },
          {
            title: 'Personalized Flows with Conditional Logic',
            desc: `Create multiple flows and tailor unique experiences based on users' choices. Make your flow smarter by letting it react dynamically to users’ answers.`,
            videoSrc: {
              desktop: 'https://assts.stories.sc/website/desktop/flowcondtion.mp4',
              mobile: 'https://assts.stories.sc/website/mobile/flowcondtion.mp4',
            },
            poster: { desktop: 'https://assts.stories.sc/website/poster/flowcondtion.jpg' },
          },
        ],
      },
      solutions: {
        title: `Who is <br/>Storycards for?`,
        items: [
          {
            title: 'Startups and Tech Companies',
            text: 'Streamline your product development process, test ideas quickly, and collect valuable user data—all without writing a single line of code. <br/Accelerate marketing efforts by generating high-quality leads, improving KPIs, and focusing on strategy instead of investing time and resources in building marketing technology.',
          },
          {
            title: 'Brands',
            text: 'Create personalized, data-driven experiences that engage their audience and drive results. <br/>Design interactive products like forms, quizzes, and surveys that align with your brand identity—all without developers. Gain real-time insights to improve KPIs and build stronger customer connections.',
          },
          {
            title: 'Advertising and Digital Agencies',
            text: 'Empower your creative team to bring ideas to life with zero development costs, maximizing profitability. Deliver tailored solutions, optimize campaigns, and unlock new creative possibilities for your clients.',
          },
        ],
      },
      portfolio: {
        title: 'Made in Storycards',
        subtitle:
          'Used by over 10 million users monthly, Storycards is the go-to platform for enterprise teams to create products that collect data and deliver better results.',
      },
      ai: {
        title: 'Your Powerful AI Data Collection Machine',
        subtitle: `Transform enterprise data collection with the power of Storycards' AI.`,
        items: [
          {
            title: 'Paste your URL and see the magic happen',
            desc: "Storycards AI engine dives deep into your site's content and crafts a unique engagement product related to your content tailored perfectly to your site’s design guidelines.",
            videoSrc: {
              desktop: 'https://assts.stories.sc/website/desktop/ai_by_url.mp4',
              mobile: 'https://assts.stories.sc/website/mobile/ai_by_url.mp4',
            },
            poster: { desktop: 'https://assts.stories.sc/website/poster/ai_by_url.jpg' },
          },
          {
            title: 'Turn your prompt into a playful product',
            desc: 'Create customized content for your products automatically by entering your specific topic. Our AI engine automatically writes content, designs images, and creates the game logic.',
            videoSrc: {
              desktop: 'https://assts.stories.sc/website/desktop/aiplayful.mp4',
              mobile: 'https://assts.stories.sc/website/mobile/aiplayful.mp4',
            },
            poster: { desktop: 'https://assts.stories.sc/website/poster/aiplayful.jpg' },
          },
          {
            title: 'Gain better performance with Storycards AI',
            desc: `Storycards AI actively monitors user interactions with your products, continuously refining content and design to enhance engagement and conversion rates. This results in strengthened brand loyalty and increased revenue.`,
            videoSrc: {
              desktop:
                'https://player.vimeo.com/progressive_redirect/playback/912106329/rendition/720p/file.mp4?loc=external&log_user=0&signature=854c7a80f1452fe55a6d39e7da9f9dd4d1cbe9e730aac6567ea21c71821c108d',
            },
          },
          {
            title: 'Assign your design to our AI',
            desc: 'No more wasted hours on design – our AI automates it all. By detecting your brand’s design guidelines directly from your website, it crafts professional, visually stunning engagement products with precision.',
            videoSrc: {
              desktop:
                'https://player.vimeo.com/progressive_redirect/playback/912108327/rendition/720p/file.mp4?loc=external&log_user=0&signature=73153747d24f63e610db99527da12dcf02873f37e018e8ab0ec331aec6999214',
            },
          },
        ],
      },
      noCodeSolutions: {
        title: 'Enterprise-Grade <br/> No-Code Solutions',
        items: [
          {
            title: `Your Data, <br/>Your Control`,
            text: `Storycards ensures compliance by seamlessly transferring data in real-time to your database or CRM. This keeps you fully protected from regulatory risks while maintaining total control over your users' information.`,
          },
          {
            title: `Seamlessly Integrated <br/> for Your Needs`,
            text: 'Storycards products integrate seamlessly into your ecosystem, functioning as if custom-built by your team. Enjoy full compatibility, effortless data sharing, and connected experiences without development overhead.',
          },
          {
            title: `Seamless Team <br/> Collaboration`,
            text: `Easily manage your organization. Create teams, assign roles, and control access.`,
          },
          {
            title: `Effortless Publishing and <br/> Scalability`,
            text: `Publish your products in one click. Our hosting scales with your traffic and ensures 99.9% uptime.`,
          },
          {
            title: `Designer-Focused Studio, <br/> Content-Friendly Editor`,
            text: `Designers create templates; content editors use them to build products. Set permissions to ensure team members can edit only what you allow.`,
          },
          {
            title: `Organization Product<br/> Library`,
            text: `Access your organization’s product library to reuse templates or start with previously created products. Save time and ensure consistent quality.`,
          },
          {
            title: `VIP Support`,
            text: `Get a dedicated expert to guide you through every step—from planning to optimizing results. Our hands-on support ensures your success.`,
          },
          {
            title: `Compliance and Security`,
            text: `Meet all regulatory requirements with ease. Storycards provides enterprise-grade security, ensuring data protection and regulatory compliance.`,
          },
        ],
      },
      integrationTools: {
        title: 'Easy Integration for All Your Daily Tools',
        text: `Integrate with over 1,500 apps using Zapier & Make, or connect directly through Storycards' webhooks.`,
      },
    },
    hosting: {
      topSection: {
        title: 'Hosting built for data at massive scale',
        subTitle:
          'Experience hosting designed for high-performance data. Handle millions of requests with speed, reliability, scalability, and enterprise-grade security.',
      },
      subtitle: 'Storycards securely processes and manages billions of page views each month.',
      description: '',
      infoSection: [
        {
          title: '99% hosting uptime',
          text: `Unleash the full potential of your data-driven products with unparalleled reliability. Our hosting guarantees enterprise-grade 99% uptime, ensuring your business never skips a beat.`,
        },
        {
          title: 'Global content delivery network (CDN)',
          text: `Your data products, delivered anywhere, anytime, with blazing speed and exceptional performance—no matter where your audience is.`,
        },
        {
          title: 'Advanced HTTP standards support',
          text: `Built for speed and security, our hosting supports cutting-edge HTTP/2 and HTTP/3 protocols to keep your products fast and trustworthy.`,
        },
        {
          title: 'Powered by leading cloud infrastructure',
          text: `Experience the reliability of Amazon Web Services (AWS) combined with the speed of Cloudflare’s global CDN. Your products are backed by the best in the business.`,
        },
        {
          title: 'Effortless, worry-free hosting',
          text: `Focus on innovation, not infrastructure. With zero maintenance required, you can publish your products and leave the rest to us.`,
        },
        {
          title: 'Ready for any traffic surge',
          text: `Whether you're scaling up or experiencing a viral moment, our hosting is ready to handle any traffic volume with ease.`,
        },
        {
          title: 'Optimized images for seamless performance',
          text: `Every visual element is automatically optimized for peak performance, ensuring your products always look and function their best.`,
        },
        {
          title: 'Built-in SSL/TLS certificates',
          text: `Every product you publish comes with free SSL and TLS, providing your audience with a secure experience while boosting your SEO rankings.`,
        },
        {
          title: 'Unmatched security for peace of mind',
          text: `Your data is protected with enterprise-level security, including DDoS attack prevention and advanced bot filtering, so you can focus on growing your business.`,
        },
        {
          title: 'Real-time data transfer with data bridge',
          text: `Connect directly to your databases or servers with our Data Bridge. Your data flows securely in real-time, without ever being stored on our cloud.`,
        },
        {
          title: 'Compliance and advanced security',
          text: `We meet the highest security standards and are fully GDPR compliant, giving you and your customers the confidence to innovate without limits.`,
        },
        {
          title: 'Dynamic video streaming',
          text: `Seamlessly integrate high-quality video into your data products with adaptive bitrate streaming, low-latency delivery, and HLS support.`,
        },
        {
          title: 'Image auto-optimize',
          text: `Automatically optimize images for faster loading times without compromising quality. Ideal for improving performance on all devices.`,
        },
        {
          title: 'Image serve auto format',
          text: `Deliver images in the best format (e.g., WebP, PNG, JPEG) automatically based on the user's browser and device capabilities.`,
        },
        {
          title: 'Deliver the best image size by device',
          text: `Serve appropriately scaled images tailored for desktop, tablet, or mobile to reduce load times and enhance user experience.`,
        },
      ],
    },
    security: {
      topSection: {
        title: 'Your Data, Your Rules, Fully Secured.',
        subTitle:
          'Our enterprise-grade security ensures the data you collect is protected, empowering your no-code creations to thrive.',
      },
      texts: [
        {
          title: 'Security Built for Enterprise-Grade Data Products',
          paragrahs: [
            {
              title: 'Publish with Confidence',
              text: 'With Storycards, clients can publish their digital products on our cloud infrastructure, eliminating the need for self-hosted solutions while maintaining enterprise-level security.',
            },
            {
              title: 'Customizable Security Policies',
              text: 'Every organization can configure its unique security settings, such as password length requirements, two-factor authentication, and role-based access control, ensuring compliance with internal and external regulations.',
            },
            {
              title: 'Intelligent Permissions Interface',
              text: 'Manage user roles and permissions effortlessly within your product. Control who can access specific features or data, and safeguard sensitive information with precision.',
            },
          ],
        },
        {
          title: 'Data Bridge',
          paragrahs: [
            {
              text: 'Your data remains yours. With our Data Bridge feature, you can seamlessly transfer collected data directly to your servers or databases without it being stored on the Storycards cloud. This ensures complete control and aligns with stringent data protection standards.',
            },
            {
              text: 'Even if you choose to collect and store data on our cloud, you retain full control. You can delete any of your data at any time, giving you the flexibility and assurance to manage your information as you see fit.',
            },
          ],
        },
        {
          title: 'Why Choose Storycards for Secure NO CODE Data Products?',
          paragrahs: [
            {
              title: 'Purpose-Built for Data',
              text: 'Unlike traditional NO CODE platforms designed for websites, Storycards specializes in creating data-driven products with embedded tools for data collection and analysis.',
            },
            {
              title: 'Compliance & Control',
              text: 'Enterprise-level features, such as single sign-on (SSO), customizable security protocols, and API management, ensure compliance with the highest security standards.',
            },
            {
              title: 'Scalability',
              text: 'From small-scale deployments to enterprise-grade solutions, our platform adapts to your needs without compromising performance or security.',
            },
          ],
        },
        {
          title: 'How Storycards Ensures Your Security',
          paragrahs: [
            {
              title: 'Encryption Standards',
              text: 'All data transfers use HTTPS with TLS 1.2+, and all products include built-in SSL certificates for added security.',
            },
            {
              title: 'Secure Authentication Options',
              text: 'We offer advanced login security, including 2-step verification via email, SMS, alongside enterprise-grade SSO.',
            },
            {
              title: 'Fraud Prevention and Threat Intelligence',
              text: 'Our AI-driven threat detection and prevention system monitors potential vulnerabilities, reducing risks and ensuring secure operations.',
            },
          ],
        },
      ],
      iconsBlock: [
        {
          title: 'Always-On DDoS Protection',
          text: 'Storycards defends against Distributed Denial of Service (DDoS) attacks of any size, ensuring uninterrupted performance and product availability.',
        },
        {
          title: 'End-to-End Encryption',
          text: 'We secure your data in transit using HTTPS and TLS 1.2+, while data at rest is protected with AES-256 encryption, the highest standard available.',
        },
        {
          title: 'Firewall With Intelligent Filtering',
          text: 'Our Web Application Firewall (WAF) uses machine learning to identify and block malicious traffic while allowing legitimate users seamless access.',
        },
        {
          title: 'Real-Time Monitoring',
          text: 'Our platform is monitored 24/7, enabling instant detection and mitigation of potential threats. This proactive approach lets you focus on building innovative products without worrying about security breaches.',
        },
      ],
      iconsBlockTitle: 'Advanced Threat Protection',
    },
    api: {
      topSection: {
        title: 'Your API Gateway to Enterprise Data.',
        subTitle:
          'Storycards’ API gives enterprises full ownership of their data—bridging no-code simplicity with enterprise-level security and flexibility. Every interaction, every event, every click is delivered directly to your systems, ensuring compliance, control, and peace of mind.',
      },
      texts: [
        {
          title: 'Why Enterprises Choose the Storycards API',
          paragrahs: [
            {
              title: 'Security First',
              text: 'Our client-to-backend architecture ensures sensitive user data flows directly into your organization’s secured systems. Nothing is stored or processed by third parties—your compliance, privacy, and governance rules remain intact. This model aligns with GDPR, SOC 2, and other enterprise privacy requirements.',
            },
            {
              title: 'Real-Time Data Flow',
              text: 'Get instant visibility into how users interact with your digital products. Capture every click, form submission, navigation, or performance metric in real time—so you can react immediately, optimize faster, and fuel downstream systems without delays.',
            },
            {
              title: 'Total Flexibility',
              text: 'Unlike rigid APIs, Storycards gives you complete freedom: Choose which events to listen to (answers, page views, performance metrics, etc.), Transmit data in any format (JSON, XML, custom payloads) and use your preferred tools—whether it’s fetch, axios, or your enterprise integration stack',
            },
          ],
        },
        {
          title: 'Your developers stay in full control while business teams keep enjoying no-code speed.',
          paragrahs: [
            {
              text: 'Storycards API makes it effortless to connect with CRM, marketing automation, analytics platforms, and internal databases. Whether you want to trigger a Salesforce update, log to Snowflake, or push events to Google Analytics, our API adapts to your workflows—not the other way around.',
            },
          ],
        },
      ],
      iconsBlock: [
        {
          title: 'Client-to-Backend Model',
          text: 'Events are triggered on the client side and sent straight to your backend.',
        },
        {
          title: 'Event-Based Architecture',
          text: 'Every user interaction—from navigation to form completion—is mapped as an event you can process.',
        },
        {
          title: 'Your Logic, Your Way',
          text: 'Decide what happens when data arrives—store it, analyze it, enrich it, or activate workflows in real time.',
        },
        {
          title: 'Under your policies',
          text: 'This ensures your data remains in your ecosystem, under your policies, without compromising performance or user experience.',
        },
      ],
      iconsBlockTitle: 'How It Works',
    },
  },
  he: {},
};

type Supportedlanguages = 'en' | 'he';

class Translator {
  private lang: Supportedlanguages;

  constructor(lang: Supportedlanguages) {
    this.lang = lang;
  }

  /**
   * Set traslator language
   * @param lang one of the supported languages
   */
  setLang(lang: Supportedlanguages) {
    this.lang = lang;
  }

  /**
   * Get text by provided path
   * @param path Path to required text, e.g: 'SCEditor.title'
   * @param replace Pattern to replace variables, e.g:
   * - TEXTS: { sample.title: 'Hello {{name}}' };
   * - replace: { name: 'Master' }
   * - result: 'Hello Master' ☠️
   */
  text(path: string, replace: object = {}) {
    let str = _.get(TEXTS, `${this.lang}.${path}`, path);

    _.forEach(replace, (value, key) => {
      str = str.replace(new RegExp(`{{${key}}}`, 'g'), value);
    });

    return str;
  }
}

export default new Translator('en');
